import * as THREE from 'three';

export class ThresholdEffect extends THREE.Group {
    constructor() {
        super();
        
        // Create the permanent ring
        this.createRing();
        
        // Setup particle system for bursts
        this.createParticleSystem();
    }

    createRing() {
        // Larger ring (inner radius 40, outer 42)
        const ringGeometry = new THREE.RingGeometry(40, 42, 64);
        const ringMaterial = new THREE.MeshBasicMaterial({
            color: 0x4a90e2,
            transparent: true,
            opacity: 0.4,
            side: THREE.DoubleSide,
            blending: THREE.AdditiveBlending
        });
        
        this.ring = new THREE.Mesh(ringGeometry, ringMaterial);
        this.ring.rotation.x = Math.PI / 2; // Lay flat
        this.ring.position.z = -100; // At threshold position
        this.add(this.ring);
    }

    createParticleSystem() {
        const particleCount = 150; // More particles
        const positions = new Float32Array(particleCount * 3);
        const velocities = [];
        
        // Create initial particle positions in a ring
        for (let i = 0; i < particleCount; i++) {
            const angle = (i / particleCount) * Math.PI * 2;
            const radius = 40; // Match ring radius
            
            positions[i * 3] = Math.cos(angle) * radius;     // x
            positions[i * 3 + 1] = Math.sin(angle) * radius; // y
            positions[i * 3 + 2] = -100;                     // z
            
            // Store velocities for animation
            velocities.push({
                x: Math.cos(angle) * 0.3, // Slower expansion
                y: Math.sin(angle) * 0.3,
                z: 0
            });
        };
        
        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        
        const material = new THREE.PointsMaterial({
            color: 0x4a90e2,
            size: 0.4, // Larger particles
            transparent: true,
            opacity: 0,
            blending: THREE.AdditiveBlending
        });
        
        this.particles = new THREE.Points(geometry, material);
        this.particles.velocities = velocities;
        this.particles.active = false;
        this.add(this.particles);
    }

    trigger() {
        // Reset particles to ring position
        const positions = this.particles.geometry.attributes.position.array;
        for (let i = 0; i < positions.length; i += 3) {
            const angle = (i / positions.length) * Math.PI * 2;
            const radius = 15;
            
            positions[i] = Math.cos(angle) * radius;     // x
            positions[i + 1] = Math.sin(angle) * radius; // y
            positions[i + 2] = -100;                     // z
        }
        
        this.particles.geometry.attributes.position.needsUpdate = true;
        this.particles.material.opacity = 1;
        this.particles.active = true;
        
        // Pulse the ring
        this.ring.material.opacity = 0.8;
        this.ringPulseStart = Date.now();
    }

    update() {
        // Update ring pulse animation
        if (this.ringPulseStart) {
            const elapsed = Date.now() - this.ringPulseStart;
            if (elapsed < 2000) { // 2 second animation
                // Ease out quadratic
                const progress = elapsed / 2000; // 2 second duration
                const easeOut = 1 - (1 - progress) * (1 - progress);
                this.ring.material.opacity = 0.8 - (0.5 * easeOut);
            } else {
                this.ring.material.opacity = 0.3;
                this.ringPulseStart = null;
            }
        }

        if (!this.particles.active) return;
        
        const positions = this.particles.geometry.attributes.position.array;
        let allParticlesGone = true;
        
        // Update particle positions
        for (let i = 0; i < positions.length; i += 3) {
            const idx = i / 3;
            const velocity = this.particles.velocities[idx];
            
            positions[i] += velocity.x;     // x
            positions[i + 1] += velocity.y; // y
            
            // Check if any particles are still visible
            if (Math.abs(positions[i]) < 30 && Math.abs(positions[i + 1]) < 30) {
                allParticlesGone = false;
            }
        }
        
        this.particles.geometry.attributes.position.needsUpdate = true;
        
        // Fade out particles
        if (this.particles.material.opacity > 0) {
            this.particles.material.opacity -= 0.01; // Slower fade
        }
        
        // Deactivate if all particles are gone
        if (allParticlesGone || this.particles.material.opacity <= 0) {
            this.particles.active = false;
        }
    }
}
